import { fetchAuthSession } from 'aws-amplify/auth';
import { useEffect, useState } from 'react';
import { PRICE_PROXY_URL } from '@/env';

export type SymbolConfig = {
  ticker: string;
  dataset: string;
  type: 'equity' | 'future';
};

let cached: SymbolConfig[] | null = null;
let inflight: Promise<SymbolConfig[]> | null = null;

async function fetchSymbols(): Promise<SymbolConfig[]> {
  if (!PRICE_PROXY_URL) return [];

  const baseUrl = PRICE_PROXY_URL.replace(/\/$/, '');

  const headers: Record<string, string> = {};
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (token) headers['Authorization'] = `Cognito ${token}`;
  } catch {
    // proceed without auth
  }

  const resp = await fetch(`${baseUrl}/symbols`, { headers });
  if (!resp.ok) return [];
  return resp.json();
}

function ensureFetch(): Promise<SymbolConfig[]> {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = fetchSymbols()
      .then((data) => {
        cached = data;
        return data;
      })
      .catch(() => {
        inflight = null;
        return [] as SymbolConfig[];
      });
  }
  return inflight;
}

export function useDatabentoSymbols() {
  const [symbols, setSymbols] = useState<SymbolConfig[]>(cached ?? []);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (cached) return;

    let cancelled = false;
    ensureFetch().then((data) => {
      if (!cancelled) {
        setSymbols(data);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { symbols, loading };
}
